import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Loading from "../components/Loading";
import AlertMessage from "../components/Alert";
import api from "../services/api";
import { url } from "../utils/pubUrl";
import "./OrderServer.css";
import toast from "react-hot-toast";
import "../styles/global.css";
import OrderImageSection from "../components/OrderForm/OrderImageSection";
import OrderOptionsSection from "../components/OrderForm/OrderOptionsSection";
import RegionDropdown from "../components/OrderForm/RegionDropdown";
import PlanDropdown from "../components/OrderForm/PlanDropdown";
import PaymentModeDropdown from "../components/common/PaymentModeDropdown";
import { useAuth } from "../context/AuthContext";
import { generateRandomVpsName } from "../components/OrderForm/orderUtils";

const OrderServer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const ORDER_DRAFT_STORAGE_KEY = "orderServerDraft:v1";
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // const [success, setSuccess] = useState("");

  // Remove tabs - show all sections in scrollable layout
  // const [activeTab, setActiveTab] = useState("image");

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    region: "",
    size: "",
    image: "",
    imageVersion: "",
    ssh_keys: [],
    backups: false,
    monitoring: true,
    hostname: "",
  });

  // Options
  const [regions, setRegions] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [applications, setApplications] = useState([]);
  // const [sshKeys, setSshKeys] = useState([]);

  // Grouped images
  const [imageGroups, setImageGroups] = useState({});
  const [selectedOS, setSelectedOS] = useState(null);
  const [availableVersions, setAvailableVersions] = useState([]);

  // CPU filter
  const [cpuFilter, setCpuFilter] = useState("regular");
  const [filteredSizes, setFilteredSizes] = useState([]);

  // ------ In component state ------
  // const [passwordTouched, setPasswordTouched] = useState(false);

  // (password removed from order flow)

  // state to track manual hostname edits
  const [hostnameEdited, setHostnameEdited] = useState(false);

  // Currency selection:
  // - URL param wins: /order?currency=USD
  // - Otherwise, if logged in and profile currency is USD, show USD
  // - Else default INR
  const currencyParamRaw = (searchParams.get("currency") || "").toUpperCase();
  const hasCurrencyOverride = currencyParamRaw === "USD";
  const [displayCurrency, setDisplayCurrency] = useState(
    hasCurrencyOverride ? "USD" : "INR",
  );

  // Size from URL: /order?size=m-2vcpu-16gb — pre-select plan when valid slug is in query
  const sizeParam = (searchParams.get("size") || "").trim().toLowerCase();
  const hasSizeParam = sizeParam.length > 0;

  useEffect(() => {
    if (hasCurrencyOverride) {
      setDisplayCurrency("USD");
      return;
    }
    const profileCurrency = (user?.currency || "").toString().toUpperCase();
    if (profileCurrency === "USD") {
      setDisplayCurrency("USD");
      return;
    }
    setDisplayCurrency("INR");
  }, [hasCurrencyOverride, user]);

  // Helper: which cpuFilter would include this size (so URL-selected plan stays visible)
  const getFilterForSize = (sizeObj) => {
    if (!sizeObj) return "regular";
    const slug = (sizeObj.slug || "").toLowerCase();
    const desc = (sizeObj.description || "").toLowerCase();
    if (
      !desc.includes("premium") &&
      !slug.includes("c-") &&
      !slug.includes("m-")
    )
      return "regular";
    if (
      desc.includes("amd") ||
      desc.includes("ryzen")
    )
      return "premium-amd";
    if (
      desc.includes("intel") ||
      desc.includes("premium") ||
      slug.includes("c-") ||
      slug.includes("g-") ||
      slug.includes("m-")
    )
      return "premium-intel";
    return "premium-intel";
  };

  // Apply size from URL when sizes are loaded (URL wins over draft for plan selection)
  useEffect(() => {
    if (!hasSizeParam || sizes.length === 0) return;
    const size = sizes.find(
      (s) => (s.slug || "").toLowerCase() === sizeParam,
    );
    if (!size) return;
    setFormData((prev) => ({ ...prev, size: size.slug }));
    setCpuFilter(getFilterForSize(size));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSizeParam, sizes]);

  const formatMoney = (amount, currencyCode) => {
    const ccy = (currencyCode || "INR").toString().toUpperCase();
    const num = Number(amount);
    if (Number.isNaN(num)) return "";
    if (ccy === "USD") return `$${num.toFixed(2)}`;
    // default INR
    return `₹${num.toFixed(2)}`;
  };

  const saveOrderDraft = (next = {}) => {
    try {
      const draft = {
        ts: Date.now(),
        formData,
        cpuFilter,
        selectedOS,
        hostnameEdited,
        ...next,
      };
      localStorage.setItem(ORDER_DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch (e) {
      // ignore storage failures
    }
  };

  const clearOrderDraft = () => {
    try {
      localStorage.removeItem(ORDER_DRAFT_STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  };

  // Restore draft (if any) so returning from /login keeps selections.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(ORDER_DRAFT_STORAGE_KEY);

      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.formData) {
          // Merge draft data, and generate random name if draft doesn't have one
          setFormData((prev) => {
            const merged = { ...prev, ...parsed.formData };
            if (!merged.name) {
              merged.name = generateRandomVpsName();
            }
            return merged;
          });
        } else {
          // Draft exists but no formData - generate random name if needed
          setFormData((prev) => {
            if (!prev.name) {
              return { ...prev, name: generateRandomVpsName() };
            }
            return prev;
          });
        }
        if (typeof parsed?.cpuFilter === "string") {
          setCpuFilter(parsed.cpuFilter);
        }
        if (
          typeof parsed?.selectedOS === "string" ||
          parsed?.selectedOS === null
        ) {
          setSelectedOS(parsed.selectedOS);
        }
        if (typeof parsed?.hostnameEdited === "boolean") {
          setHostnameEdited(parsed.hostnameEdited);
        }
      } else {
        // No draft found - generate random VPS name as default
        setFormData((prev) => {
          if (!prev.name) {
            return { ...prev, name: generateRandomVpsName() };
          }
          return prev;
        });
      }
    } catch (e) {
      // ignore parse errors - but still generate random name if needed
      setFormData((prev) => {
        if (!prev.name) {
          return { ...prev, name: generateRandomVpsName() };
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function sanitizeSlug(v) {
    return (v || "")
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }

  function computeDefaultHostname() {
    // image part
    let imagePart = "image";
    const app = applications.find((a) => a.slug === formData.image);
    if (app) imagePart = app.slug;
    else {
      const dist = distributions.find((d) => d.slug === formData.image);
      if (dist)
        imagePart = dist.slug || dist.distribution || dist.name || "image";
    }
    imagePart = sanitizeSlug(imagePart);

    // size part
    let sizePart = sanitizeSlug(formData.size || "s-1vcpu-512mb-10gb");

    // region part
    let regionPart = sanitizeSlug(formData.region || "sfo2");

    const count = 1;
    return `${imagePart}-${sizePart}-${regionPart}-${count}`;
  }

  // --- Hostname logic (no infinite update, Geist style) ---
  // Update hostname automatically when selections change, but only if user hasn't edited
  useEffect(() => {
    if (!hostnameEdited) {
      const auto = computeDefaultHostname();
      setFormData((prev) => ({ ...prev, hostname: auto }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.image,
    formData.size,
    formData.region,
    applications,
    distributions,
    hostnameEdited,
  ]);

  useEffect(() => {
    fetchOptions();
  }, []);

  // Set default region to "blr1" when regions are loaded (if no region selected)
  // This runs after draft restoration, so it won't override user's saved draft
  useEffect(() => {
    if (regions.length > 0 && !formData.region) {
      const blr1Region = regions.find((r) => r.slug === "blr1");
      if (blr1Region) {
        setFormData((prev) => ({ ...prev, region: "blr1" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions]);

  // Keep a draft in localStorage while user edits, so login redirect can restore state.
  useEffect(() => {
    saveOrderDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, cpuFilter, selectedOS, hostnameEdited]);

  // Normalize distributions into families (Ubuntu, Debian, etc.)
  useEffect(() => {
    if (distributions.length > 0) {
      const familyOf = (img) => {
        const base = (img.distribution || img.name || img.slug || "")
          .toString()
          .toLowerCase();
        if (base.includes("ubuntu")) return "Ubuntu";
        if (base.includes("debian")) return "Debian";
        if (base.includes("fedora")) return "Fedora";
        if (base.includes("centos")) return "CentOS";
        if (base.includes("almalinux")) return "AlmaLinux";
        if (base.includes("rocky")) return "Rocky Linux";
        if (base.includes("freebsd")) return "FreeBSD";
        if (base.includes("opensuse")) return "openSUSE";
        return img.distribution || "Other";
      };

      const groups = {};
      distributions.forEach((img) => {
        const family = familyOf(img);
        if (!groups[family]) groups[family] = [];
        groups[family].push(img);
      });

      // Sort versions descending by created date/name if available
      Object.keys(groups).forEach((k) => {
        groups[k].sort((a, b) => {
          const an = a.name || "";
          const bn = b.name || "";
          return bn.localeCompare(an, undefined, { numeric: true });
        });
      });

      setImageGroups(groups);
    }
  }, [distributions]);

  // (removed legacy hostname effect)

  const fetchOptions = async () => {
    try {
      const [regionsRes, sizesRes, distRes, appsRes] = await Promise.all([
        api.get("/resources/regions"),
        api.get("/resources/sizes"),
        api.get("/resources/distributions"),
        api.get("/resources/applications"),
      ]);

      setRegions(regionsRes.data);
      setSizes(sizesRes.data);
      setDistributions(distRes.data);
      setApplications(appsRes.data);
    } catch (err) {
      setError("Failed to load options");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter sizes based on CPU type
  useEffect(() => {
    if (sizes.length === 0) {
      setFilteredSizes([]);
      return;
    }

    let filtered = [];

    switch (cpuFilter) {
      case "regular":
        // Regular sizes (basic SSD - typically don't have 'premium' in description)
        filtered = sizes.filter(
          (size) =>
            !size.description?.toLowerCase().includes("premium") &&
            !size.slug?.includes("c-") &&
            !size.slug?.includes("m-"),
        );
        break;
      case "premium-intel":
        // Premium Intel (typically have Intel in description or specific slugs)
        filtered = sizes.filter(
          (size) =>
            size.description?.toLowerCase().includes("intel") ||
            size.description?.toLowerCase().includes("premium") ||
            size.slug?.includes("c-") ||
            size.slug?.includes("g-"),
        );
        break;
      case "premium-amd":
        // Premium AMD (typically have AMD in description)
        filtered = sizes.filter(
          (size) =>
            size.description?.toLowerCase().includes("amd") ||
            size.description?.toLowerCase().includes("ryzen"),
        );
        break;
      default:
        filtered = sizes;
    }

    // If no matches, show all sizes
    if (filtered.length === 0) {
      filtered = sizes;
    }

    setFilteredSizes(filtered);

    // Clear selected size if it's not in filtered list
    if (formData.size && !filtered.find((s) => s.slug === formData.size)) {
      setFormData((prev) => ({ ...prev, size: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpuFilter, sizes]);

  // Helper: derive current image/application and min disk
  const getCurrentImageMeta = () => {
    const app = applications.find((a) => a.slug === formData.image);
    if (app) return app;
    const dist = distributions.find((d) => d.slug === formData.image);
    return dist || null;
  };

  const requiredMinDisk = (() => {
    const img = getCurrentImageMeta();
    return img && typeof img.min_disk_size === "number" ? img.min_disk_size : 0;
  })();

  const isSelectedSizeCompatible = () => {
    if (!formData.size) return true;
    const s = sizes.find((sz) => sz.slug === formData.size);
    if (!s) return false;
    if (requiredMinDisk && (s.disk || 0) < requiredMinDisk) return false;
    return true;
  };

  // If currently selected size violates min_disk_size, clear it
  useEffect(() => {
    if (!formData.size) return;
    const s = sizes.find((sz) => sz.slug === formData.size);
    if (!s) return;
    if (requiredMinDisk && s.disk < requiredMinDisk) {
      setFormData((prev) => ({ ...prev, size: "" }));
      toast.error(
        `Selected image requires at least ${requiredMinDisk}GB disk. Choose a larger plan.`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.image, requiredMinDisk]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOSSelect = (osFamily) => {
    setSelectedOS(osFamily);
    const versions = imageGroups[osFamily] || [];
    const orderedVersions = [...versions].reverse();
    setAvailableVersions(orderedVersions);
    if (orderedVersions.length > 0) {
      handleVersionSelect(orderedVersions[0]);
    } else {
      handleChange("image", "");
      handleChange("imageVersion", "");
    }
  };

  const handleVersionSelect = (version) => {
    const slug = version.slug || version.id;
    handleChange("image", slug);
    handleChange("imageVersion", slug);
  };

  const handleApplicationSelect = (slug) => {
    setSelectedOS(null);
    setAvailableVersions([]);
    handleChange("image", slug);
    handleChange("imageVersion", slug);
  };

  const [orderResult, setOrderResult] = useState(null);
  const [orderStatus, setOrderStatus] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [orderDetails, setOrderDetails] = useState(null);
  const [invoiceBreakdown, setInvoiceBreakdown] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState("razorpay");

  useEffect(() => {
    if (orderResult) {
      const nextStatus =
        typeof orderResult.status === "string"
          ? orderResult.status.toLowerCase()
          : "pending";
      setOrderStatus(nextStatus || "pending");
    } else {
      setOrderStatus("");
      setInvoiceStatus("");
      setOrderDetails(null);
    }
  }, [orderResult]);

  const formatStatusLabel = (value) => {
    if (!value) return "";
    const normalized = value.toString().toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const getStatusBadgeClass = (status) => {
    const normalized = status?.toString().toLowerCase();
    const validStatuses = [
      "pending",
      "paid",
      "activated",
      "failed",
      "destroyed",
    ];
    if (normalized && validStatuses.includes(normalized)) {
      return `status-badge ${normalized}`;
    }
    return "status-badge";
  };

  const handleLoginRedirect = () => {
    saveOrderDraft();
    const returnTo = `${location.pathname}${location.search || ""}`;
    const params = new URLSearchParams({ returnTo });
    navigate(`/login?${params.toString()}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      handleLoginRedirect();
      return;
    }
    setSubmitting(true);
    setError("");

    if (
      !formData.image ||
      !formData.region ||
      !formData.size ||
      !formData.name
    ) {
      toast.error("Please complete all required fields");
      setSubmitting(false);
      return;
    }

    // Hard-block submit if current size doesn't satisfy image requirements
    if (!isSelectedSizeCompatible()) {
      toast.error(
        requiredMinDisk
          ? `Selected image requires at least ${requiredMinDisk}GB disk. Choose a larger plan.`
          : "Selected plan is not compatible with the chosen image. Please choose another plan.",
      );
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        ...formData,
      };

      const response = await api.post("/droplets/order", payload);

      if (response.data.order) {
        // Store order result to show invoice
        setOrderResult(response.data.order);
        setInvoiceStatus("");
        setOrderDetails(null);
        setOrderStatus(
          typeof response.data.order.status === "string"
            ? response.data.order.status.toLowerCase()
            : "pending",
        );
        toast.success(response.data.message || "Order created successfully!");
        clearOrderDraft();
      } else {
        toast.success("Server ordered successfully! Redirecting...");
        setTimeout(() => navigate("/"), 1500);
        clearOrderDraft();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to order server");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentClick = () => {
    if (!orderResult?.id) return;
    // Create/Reuse gateway payment and open it based on selected payment mode.
    api
      .post("/payments/create", {
        orderId: orderResult.id,
        provider: paymentProvider,
      })
      .then((res) => {
        const urlToOpen = res.data?.url;
        if (urlToOpen) {
          window.open(urlToOpen, "_blank");
        } else {
          toast.error("Payment link not available. Please try again.");
        }
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Failed to start payment");
      });
  };

  const handleViewOrders = () => {
    navigate("/orders");
  };

  // Auto-load invoice details for breakdown (no manual refresh button needed)
  useEffect(() => {
    const orderId = orderResult?.id;
    if (!orderId) return;
    let cancelled = false;
    setLoadingInvoice(true);
    api
      .get(`/whmcs/order/${orderId}`)
      .then((res) => {
        if (cancelled) return;
        const inv = res.data?.invoice || null;
        if (!inv) return;

        const subtotal = parseFloat(inv.subtotal ?? 0) || 0;
        const tax = parseFloat(inv.tax ?? 0) || 0;
        const tax2 = parseFloat(inv.tax2 ?? 0) || 0;
        const total = parseFloat(inv.total ?? inv.balance ?? 0) || 0;
        const items = Array.isArray(inv.items?.item) ? inv.items.item : [];

        const baseItem = items.find((i) =>
          String(i?.description || "")
            .toLowerCase()
            .includes("droplet:"),
        );
        const backupItem = items.find((i) =>
          String(i?.description || "")
            .toLowerCase()
            .includes("backup service"),
        );

        setInvoiceBreakdown({
          // Invoice currency should never fall back to displayCurrency (query param).
          // If WHMCS doesn't provide currency code, default to INR.
          currency:
            inv.currencycode ||
            inv.currency_code ||
            inv.currency ||
            orderResult.currency ||
            "INR",
          subtotal,
          tax: tax + tax2,
          total,
          base: baseItem ? parseFloat(baseItem.amount || 0) || 0 : null,
          backup: backupItem ? parseFloat(backupItem.amount || 0) || 0 : null,
        });
      })
      .catch(() => {})
      .finally(() => {
        if (cancelled) return;
        setLoadingInvoice(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderResult?.id]);

  // (removed manual refresh in sidebar; status checks live in /orders)

  const getRegionIcon = (regionSlug) => {
    const regionToCountryMap = {
      nyc: "us",
      sfo: "us",
      tor: "ca",
      lon: "gb",
      ams: "nl",
      fra: "de",
      sgp: "sg",
      blr: "in",
      syd: "au",
      atl: "us",
    };
    // Extract first 3 characters to match region prefix (e.g., "nyc1" -> "nyc")
    const prefix = String(regionSlug || "")
      .toLowerCase()
      .substring(0, 3);
    const countryCode = regionToCountryMap[prefix];
    if (countryCode) {
      return (
        <img
          src={url(`icons/flags/${countryCode}.png`)}
          alt={regionSlug}
          className="order-region-flag"
        />
      );
    }
    return (
      <img
        src={url("icons/flags/us.png")}
        alt={regionSlug}
        className="order-region-flag"
      />
    );
  };

  const getSizePrice = (sizeSlug) => {
    const size = sizes.find((s) => s.slug === sizeSlug);
    if (!size) return "";
    if (displayCurrency === "USD") {
      const usd =
        size?.pricing?.usdWithMargin ??
        size?.pricing?.usd ??
        (typeof size?.price_monthly === "string"
          ? parseFloat(size.price_monthly)
          : size?.price_monthly);
      const num = Number(usd);
      if (!Number.isNaN(num)) return `$${num.toFixed(2)}/mo`;
      return "$/mo";
    }
    if (size.pricing && typeof size.pricing.inr === "number") {
      return `₹${size.pricing.inr}/mo`;
    }
    return `₹${size.price_monthly}/mo`;
  };

  const isFormValid = () => {
    return (
      formData.image &&
      formData.region &&
      formData.size &&
      formData.name &&
      isSelectedSizeCompatible()
    );
  };

  if (loading) return <Loading />;

  return (
    <>
      <div className="cart-page order-page-wrapper">
        {/* Header */}
        <div className="cart-header">
          <div className="cart-header-content">
            <div className="cart-logo">
              <img
                src="/logo.png"
                alt="Logo"
                onError={(e) => (e.target.style.display = "none")}
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="cart-container">
          {error && <AlertMessage type="danger" message={error} />}

          <div className="cart-layout">
            {/* Left Column - Configuration */}
            <div className="cart-left">
              <h1 className="cart-title">Your Cart</h1>

              {/* Product Card */}
              <div className="cart-product-card">
                <div className="product-header">
                  <h2 className="product-title">Cloud VPS</h2>
                  <p className="product-subtitle">
                    Deploy and scale your applications with Cyberin
                  </p>
                </div>

                <div className="config-content-scrollable">
                  {/* Operating System / Image Section */}
                  <div className="config-section">
                    <label className="config-label">
                      Select operating system
                    </label>
                    <p className="config-description">
                      Select OS, control panel, or application that you want to
                      install. You can change this later through VPS dashboard.
                    </p>

                    <OrderImageSection
                      imageGroups={imageGroups}
                      handleOSSelect={handleOSSelect}
                      selectedOS={selectedOS}
                      availableVersions={availableVersions}
                      handleVersionSelect={handleVersionSelect}
                      formData={formData}
                      handleApplicationSelect={handleApplicationSelect}
                      applications={applications}
                      distributions={distributions}
                    />
                  </div>

                  {/* Region Section */}
                  <div className="config-section">
                    <label className="config-label">
                      Select server location
                    </label>
                    <p className="config-description">
                      Choose a region closest to your users for best performance
                    </p>

                    <RegionDropdown
                      regions={regions}
                      value={formData.region}
                      onChange={(slug) => handleChange("region", slug)}
                      getRegionIcon={getRegionIcon}
                    />
                  </div>

                  {/* Plan / Size Section */}
                  <div className="config-section">
                    <label className="config-label">Choose Plan</label>
                    <p className="config-description">
                      Select a plan that suits your needs
                    </p>

                    <div className="cpu-options-compact">
                      <label className="cpu-options-label">CPU options</label>
                      <div className="cpu-options-pills">
                        <button
                          type="button"
                          className={`cpu-pill ${
                            cpuFilter === "regular" ? "active" : ""
                          }`}
                          onClick={() => setCpuFilter("regular")}
                        >
                          <span className="cpu-pill-name">Regular</span>
                          <span className="cpu-pill-desc">SSD</span>
                        </button>

                        <button
                          type="button"
                          className={`cpu-pill ${
                            cpuFilter === "premium-intel" ? "active" : ""
                          }`}
                          onClick={() => setCpuFilter("premium-intel")}
                        >
                          <span className="cpu-pill-name">Premium Intel</span>
                          <span className="cpu-pill-desc">NVMe</span>
                        </button>

                        <button
                          type="button"
                          className={`cpu-pill ${
                            cpuFilter === "premium-amd" ? "active" : ""
                          }`}
                          onClick={() => setCpuFilter("premium-amd")}
                        >
                          <span className="cpu-pill-name">Premium AMD</span>
                          <span className="cpu-pill-desc">NVMe</span>
                        </button>
                      </div>
                    </div>

                    <PlanDropdown
                      sizes={filteredSizes.length > 0 ? filteredSizes : sizes}
                      value={formData.size}
                      onChange={(slug) => handleChange("size", slug)}
                      requiredMinDisk={requiredMinDisk}
                      onInvalidSelect={(message) => toast.error(message)}
                      currency={displayCurrency}
                      region={formData.region}
                    />
                  </div>

                  {/* Additional Options Section */}
                  <div className="config-section">
                    <label className="config-label">Additional Options</label>
                    <OrderOptionsSection
                      formData={formData}
                      setFormData={setFormData}
                      hostnameEdited={hostnameEdited}
                      setHostnameEdited={setHostnameEdited}
                      applications={applications}
                      distributions={distributions}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="cart-right">
              <div className="order-summary-card">
                <h3 className="summary-title">Order Summary</h3>
                <div className="summary-content">
                  <div className="summary-item">
                    <div className="item-row">
                      <span className="item-label">Image:</span>
                      <span className="item-value">
                        {formData.image
                          ? distributions.find((d) => d.slug === formData.image)
                              ?.name ||
                            applications.find((a) => a.slug === formData.image)
                              ?.name ||
                            formData.image
                          : "Not selected"}
                      </span>
                    </div>
                    <div className="item-row">
                      <span className="item-label">Region:</span>
                      <span className="item-value">
                        {formData.region
                          ? regions.find((r) => r.slug === formData.region)
                              ?.name || formData.region
                          : "Not selected"}
                      </span>
                    </div>
                    <div className="item-row">
                      <span className="item-label">Plan:</span>
                      <span className="item-value">
                        {formData.size
                          ? `${formData.size} - ${getSizePrice(formData.size)}`
                          : "Not selected"}
                      </span>
                    </div>
                  </div>

                  <div className="summary-divider"></div>

                  <div className="summary-item">
                    <div className="item-row">
                      <span className="item-label">
                        <label className="addon-checkbox-inline">
                          <input
                            type="checkbox"
                            checked={formData.backups}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                backups: e.target.checked,
                              }))
                            }
                          />
                          <span>Backups</span>
                        </label>
                      </span>
                      <span className="item-value">
                        {formData.backups ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                    <div className="item-row">
                      <span className="item-label">
                        <label className="addon-checkbox-inline">
                          <input
                            type="checkbox"
                            checked={formData.monitoring}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                monitoring: e.target.checked,
                              }))
                            }
                          />
                          <span>Monitoring</span>
                        </label>
                      </span>
                      <span className="item-value">
                        {formData.monitoring ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  </div>
                </div>
                {orderResult ? (
                  <div className="summary-footer">
                    <div className="order-result-container">
                      <h4 className="order-result-title">Order Created</h4>
                      <p className="order-result-text">
                        Invoice #{orderResult.invoiceNumber}
                      </p>
                      {orderStatus && (
                        <p className="order-result-text">
                          Status:{" "}
                          <span className={getStatusBadgeClass(orderStatus)}>
                            {formatStatusLabel(orderStatus)}
                          </span>
                        </p>
                      )}
                      {invoiceStatus && (
                        <p className="order-result-text">
                          Invoice status:{" "}
                          <strong>{formatStatusLabel(invoiceStatus)}</strong>
                        </p>
                      )}
                      {orderDetails?.dropletId && (
                        <p className="order-result-text">
                          Droplet ID: <strong>{orderDetails.dropletId}</strong>
                        </p>
                      )}
                      {!orderDetails?.dropletId && (
                        <p className="order-result-price">
                          Total:{" "}
                          {formatMoney(
                            invoiceBreakdown?.total ?? orderResult.total,
                            invoiceBreakdown?.currency ||
                              orderResult.currency ||
                              displayCurrency,
                          )}
                        </p>
                      )}

                      {/* Price breakdown (from WHMCS invoice, includes GST) */}
                      {!orderDetails?.dropletId && (
                        <div className="order-invoice-breakdown">
                          <div className="summary-divider" />
                          {loadingInvoice ? (
                            <p className="order-result-text">
                              Loading invoice…
                            </p>
                          ) : invoiceBreakdown ? (
                            <div className="order-result-text">
                              <div className="item-row">
                                <span className="item-label">Subtotal:</span>
                                <span className="item-value">
                                  {formatMoney(
                                    invoiceBreakdown.subtotal,
                                    invoiceBreakdown.currency,
                                  )}
                                </span>
                              </div>
                              {invoiceBreakdown.base != null && (
                                <div className="item-row">
                                  <span className="item-label">Base:</span>
                                  <span className="item-value">
                                    {formatMoney(
                                      invoiceBreakdown.base,
                                      invoiceBreakdown.currency,
                                    )}
                                  </span>
                                </div>
                              )}
                              {invoiceBreakdown.backup != null && (
                                <div className="item-row">
                                  <span className="item-label">Backups:</span>
                                  <span className="item-value">
                                    {formatMoney(
                                      invoiceBreakdown.backup,
                                      invoiceBreakdown.currency,
                                    )}
                                  </span>
                                </div>
                              )}
                              <div className="item-row">
                                <span className="item-label">GST/Tax:</span>
                                <span className="item-value">
                                  {formatMoney(
                                    invoiceBreakdown.tax,
                                    invoiceBreakdown.currency,
                                  )}
                                </span>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      )}
                      <div className="order-result-actions">
                        {!orderDetails?.dropletId && (
                          <div className="order-paybox">
                            <Button
                              type="button"
                              variant="primary"
                              className="create-button order-paybox__pay"
                              onClick={handlePaymentClick}
                            >
                              Pay Now
                            </Button>
                            <PaymentModeDropdown
                              value={paymentProvider}
                              onChange={setPaymentProvider}
                            />
                          </div>
                        )}
                        {orderDetails?.dropletId ? (
                          <Button
                            type="button"
                            variant="btn-primary"
                            className="create-button"
                            onClick={() =>
                              navigate(`/server/${orderDetails?.dropletId}`)
                            }
                          >
                            View Droplet
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="summary-divider"></div>
                    <button
                      type="button"
                      className="continue-btn"
                      onClick={user ? handleSubmit : handleLoginRedirect}
                      disabled={submitting || !isFormValid()}
                    >
                      {submitting ? "Creating..." : "Create VPS"}
                    </button>
                    <div className="guarantee">
                      <svg
                        className="guarantee-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="guarantee-text">
                        Secure order processing
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="cart-footer">
          <div className="footer-content">
            <span className="footer-text">© 2004–2026 Cyberin</span>
            <div className="footer-links">
              <a href="https://cyberin.in/terms" className="footer-link">
                Terms of service
              </a>
              <a
                href="https://cyberin.in/privacy-policy"
                className="footer-link"
              >
                Privacy policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderServer;
